"use client";

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { galleryEvents, type GalleryEvent } from '@/lib/data';
import { getGalleryEvents } from '@/lib/actions';
import type { GalleryEventDoc } from '@/lib/types';
import { X, Calendar, GitCommit, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import Image from '@/components/Image';

export function GalleryClient() {
  // Los eventos nuevos se gestionan desde /jartinet/admin/galeria; los
  // históricos siguen en el código (fotos servidas desde /public) y van detrás.
  const [remoteEvents, setRemoteEvents] = useState<GalleryEventDoc[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getGalleryEvents().then((evs) => {
      if (!cancelled) setRemoteEvents(evs);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const allEvents: GalleryEvent[] = useMemo(
    () => [
      ...remoteEvents
        .filter((e) => (e.images?.length || 0) > 0)
        .map((e) => ({
          id: e.id,
          title: e.title,
          date: e.date,
          modVersion: e.modVersion || '',
          images: (e.images || []).map((img, i) => ({
            id: img.id || `${e.id}-${i}`,
            src: img.src,
            alt: img.alt || `${e.title} — foto ${i + 1}`,
            hint: 'minecraft cofrade',
          })),
        })),
      ...galleryEvents,
    ],
    [remoteEvents],
  );

  const activeEvent = allEvents.find((e) => e.id === activeId) || allEvents[0];

  if (!activeEvent) return null;

  const closeLightbox = () => setSelectedImageIndex(null);
  const showPrev = () =>
    setSelectedImageIndex((i) => (i === null ? null : (i - 1 + activeEvent.images.length) % activeEvent.images.length));
  const showNext = () => setSelectedImageIndex((i) => (i === null ? null : (i + 1) % activeEvent.images.length));

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden border-b border-white/8 py-20 text-center">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="container relative mx-auto max-w-3xl px-4">
          <motion.div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Images className="h-7 w-7 text-primary" />
          </motion.div>
          <motion.h1
            className="font-headline text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Galería</span>
          </motion.h1>
          <motion.p
            className="mt-4 text-base text-muted-foreground md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Los mejores momentos capturados en Jartiland, evento por evento.
          </motion.p>
        </div>
      </section>

      <div className="container mx-auto flex flex-col gap-8 px-4 py-16 md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-72 md:flex-shrink-0">
          <div className="rounded-2xl border border-white/8 bg-card/40 p-3 backdrop-blur-sm md:sticky md:top-24">
            <h2 className="px-3 py-2 text-xs font-bold uppercase tracking-[0.25em] text-primary">Eventos</h2>
            <nav className="flex flex-col gap-1">
              {allEvents.map((event) => {
                const isActive = activeEvent.id === event.id;
                return (
                  <button
                    key={event.id}
                    onClick={() => setActiveId(event.id)}
                    className={cn(
                      'group relative w-full overflow-hidden rounded-xl p-3 text-left transition-colors',
                      isActive ? 'bg-primary/15 text-foreground' : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
                    )}
                  >
                    {isActive && <span className="absolute left-0 top-0 h-full w-[3px] bg-primary" />}
                    <p className="text-sm font-semibold">{event.title}</p>
                    <div className="mt-1 flex items-center gap-3 text-xs">
                      <span className={cn('flex items-center gap-1', isActive ? 'text-primary' : 'text-muted-foreground/70')}>
                        <Calendar className="h-3 w-3" />
                        {event.date}
                      </span>
                      {event.modVersion && (
                        <span className="flex items-center gap-1 text-muted-foreground/60">
                          <GitCommit className="h-3 w-3" />v{event.modVersion}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeEvent.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="relative mb-6 overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-6 backdrop-blur-sm">
                <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-amber-500" />
                <h1 className="font-headline text-3xl font-bold md:text-4xl">{activeEvent.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {activeEvent.date}
                  </div>
                  {activeEvent.modVersion && (
                    <div className="flex items-center gap-2">
                      <GitCommit className="h-4 w-4 text-primary" />
                      Mod Cofrade v{activeEvent.modVersion}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Images className="h-4 w-4 text-primary" />
                    {activeEvent.images.length} fotos
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4">
                {activeEvent.images.map((image, index) => (
                  <motion.div
                    key={image.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => setSelectedImageIndex(index)}
                    className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-white/8 transition-colors hover:border-primary/30"
                  >
                    <Image
                      src={image.src}
                      alt={image.alt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      data-ai-hint={image.hint}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 p-4 backdrop-blur-md"
          >
            <button
              onClick={closeLightbox}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-card/60 text-foreground transition-colors hover:bg-card/90 hover:text-primary"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
              className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-card/60 text-foreground transition-colors hover:bg-card/90 hover:text-primary"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
              className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-card/60 text-foreground transition-colors hover:bg-card/90 hover:text-primary"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <motion.div
              layout
              layoutId={activeEvent.images[selectedImageIndex].src}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-full max-w-4xl overflow-hidden rounded-2xl border border-white/10"
            >
              <Image
                src={activeEvent.images[selectedImageIndex].src}
                alt="Imagen seleccionada"
                className="max-h-[85vh] max-w-full object-contain"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
